import { API_METHODS } from '@compass/client/constants/api-methods';
import { useStore } from '@store/index';

import { CASES_ANALYTICS_DISABLED_ORG_IDS } from '@/constants/feature-flags';
import { useIs100msUser } from '@/hooks/useIs100msUser';
import { usePermissionMap } from '@/hooks/usePermissions';

const hasCasesAnalyticsPermission = (permissions?: Record<string, boolean>): boolean =>
  permissions?.[API_METHODS.CasesAnalyticsService.GetCasesAnalytics] === true;

/**
 * Whether the AE / PQC Analytics page (sidebar entry and direct URL) is available. Mirrors
 * `useShouldShowAnalyticsPage`:
 *   1. 100ms users (100ms.ai / 100ms.live email): always allowed across all orgs
 *   2. Non-100ms users: allowed unless their org is in REACT_APP_CASES_ANALYTICS_DISABLED_ORG_IDS
 *   3. The user must hold the org-scoped `case_analytics` permission (org admins, and the
 *      Executive role in PQC orgs). A missing key, or a map still loading, reads as hidden.
 * UI affordance only: the API enforces the permission server-side.
 */
export const useShouldShowCasesAnalyticsPage = (contextOverride?: {
  organizationId?: string | null;
  programId?: string | null;
}): { shouldShow: boolean; isLoading: boolean } => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const effectiveOrganizationId =
    contextOverride && 'organizationId' in contextOverride
      ? contextOverride.organizationId
      : selectedOrganizationId;
  const is100msUser = useIs100msUser();
  const { data, isLoading } = usePermissionMap(contextOverride);

  const isOrgDisabled =
    !!effectiveOrganizationId && CASES_ANALYTICS_DISABLED_ORG_IDS.has(effectiveOrganizationId);
  const isAllowed = is100msUser || !isOrgDisabled;

  return {
    shouldShow: isAllowed && hasCasesAnalyticsPermission(data?.permissions),
    isLoading,
  };
};
